// @ts-nocheck
import React, { ChangeEvent, useCallback, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import debounce from "lodash/debounce";
import { css } from "styled-components";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getWishListState } from "@insite/client-framework/Store/Data/WishLists/WishListsSelectors";
import { getWishListLinesDataView } from "@insite/client-framework/Store/Data/WishListLines/WishListLinesSelectors";
import updateLoadWishListLinesParameter from "@insite/client-framework/Store/Pages/MyListDetails/Handlers/UpdateLoadWishListLinesParameter";
import loadWishListLines from "@insite/client-framework/Store/Pages/MyListDetails/Handlers/LoadWishListLines";
import setEditingSortOrder from "@insite/client-framework/Store/Pages/MyListDetails/Handlers/SetEditingSortOrder";
import translate from "@insite/client-framework/Translate";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import TextField, { TextFieldProps } from "@insite/mobius/TextField";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Search from "@insite/mobius/Icons/Search";

const mapStateToProps = (state: ApplicationState) => ({
    wishListSettings: getSettingsCollection(state).wishListSettings,
    wishListDataView: getWishListState(state, state.pages.myListDetails.wishListId),
    wishListLinesDataView: getWishListLinesDataView(state, state.pages.myListDetails.loadWishListLinesParameter),
    loadWishListLinesParameter: state.pages.myListDetails.loadWishListLinesParameter,
});

const mapDispatchToProps = {
    updateLoadWishListLinesParameter,
    loadWishListLines,
    setEditingSortOrder,
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

interface SearchOnlyStyles {
    wrapper?: { css?: ReturnType<typeof css> };
    container?: GridContainerProps;
    searchGridItem?: GridItemProps;
    search?: TextFieldProps;
}

const styles: SearchOnlyStyles = {
    wrapper: {
        css: css`
            margin: 1em 0;
        `,
    },
    container: {},
    searchGridItem: { width: [12, 12, 7, 7, 7] },
    search: { iconProps: { src: Search } },
};

const MyListsDetailsSearchOnly = ({
    wishListSettings,
    wishListDataView,
    loadWishListLinesParameter,
    updateLoadWishListLinesParameter,
    loadWishListLines,
    setEditingSortOrder,
}: Props) => {
    const [query, setQuery] = useState(loadWishListLinesParameter.query || "");

    useEffect(() => {
        setQuery("");
        setEditingSortOrder({ editingSortOrder: false });
    }, []);

    const debouncedSearch = useCallback(
        debounce((q: string) => {
            if (!wishListDataView.value) return;
            const searchText = (q || "").trim();
            updateLoadWishListLinesParameter({ query: searchText });
            loadWishListLines();
        }, 200),
        [wishListDataView, updateLoadWishListLinesParameter, loadWishListLines],
    );

    if (!wishListDataView.value || !wishListSettings) {
        return null;
    }

    const wishList = wishListDataView.value;
    const canEditWishList = (wishList.allowEdit || !wishList.isSharedList) && !wishList.isAutogenerated;

    const onSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
        const newQuery = event.target.value || "";
        setQuery(newQuery);
        debouncedSearch(newQuery);
    };

    return (
        <StyledWrapper {...styles.wrapper}>
            {/* Store Removed Code - canEditWishList condition */}
            <GridContainer {...styles.container}>
                <GridItem {...styles.searchGridItem}>
                    <TextField
                        {...styles.search}
                        placeholder={translate("Search location by keyword or item number")}
                        value={query}
                        onChange={onSearchChange}
                        data-test-selector="wishListSearch"
                        label={translate("Search items")}
                        type="search"
                    />
                </GridItem>
            </GridContainer>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(MyListsDetailsSearchOnly),
    definition: {
        group: "My Lists Details",
        displayName: "List Internal Search",
        allowedContexts: ["MyListsDetailsPage"],
    },
};

export default widgetModule;
